import { PropsWithChildren } from "react";
import { AcquisitionFlow, checkIfInValidAcquisitionFlow, enforceTwoDigitDOB, getMarketingAttributesForAcquisitions, getProgramData, TAcquisitionsMarketingAttributeArray } from "../../../utils/acquisition-utils";
import { flowDataModule } from "../../../utils/flowDataModule";
import SessionStorage from "../../../utils/SessionStorage";
import { IApplicationCtaProps } from "../ApplicationCta-RefactoredValidations";
import { TAcquisitionProviderFacade } from "../../../facades/AcquisitionProviderFacade/useAcquisitionProviderFacade";
import { TPrequalResponse } from "../../../types/responses/PrequalResponse";
import { blackboxUtils } from "../../../utils/blackbox-utils";
import { hasDefaultComponent } from "../../../utils/pageModelUtils";
import { buildSubmitFlowApplicationData, buildSubmitDataForPA, buildSubmitDataForPqSkipPqPqOfOne } from "../../../utils/acquisition-utils-buildAppData";
import { TSubmitDataPHA, TSubmitDataPqSkipPqPqOfOne, TSubmitDataPreApproved } from "../../../utils/acquisition-utils-buildAppData.types";


export type TApplicationData = { 
    'AuthorizedUser-IsAuthorizedUserSelected'?: boolean;
    'AuthorizedUser-IsAuthorizedUserSpouse'?: boolean;
    'AuthorizedUser-AuthorizedUserFirstName'?: string;
    'AuthorizedUser-AuthorizedUserMiddleInitial'?: string;
    'AuthorizedUser-AuthorizedUserLastName'?: string;
    'AuthorizedUser-AuthorizedUserDOBDay'?: string;
    'AuthorizedUser-AuthorizedUserDOBMonth'?: string;
    'AuthorizedUser-AuthorizedUserDOBYear'?: string;
    'CreditProtection-CreditProtectionSelected'?: boolean;
    'CreditProtection-CreditProtectionInitials'?: string;
    'PersonalInformation-FirstName'?: string;
    'PersonalInformation-MiddleInitial'?: string;
    'PersonalInformation-LastName'?: string;
    'PersonalInformation-NameSuffix'?: string;
    'PersonalInformation-EmailAddress'?: string;
    'PersonalInformation-MailingAddressLine1'?: string;
    'PersonalInformation-MailingAddressLine2'?: string;
    'PersonalInformation-MailingCity'?: string;
    'PersonalInformation-MailingState'?: string;
    'PersonalInformation-MailingZipCode'?: string;
    'PersonalInformation-HomeAddressLine1'?: string;
    'PersonalInformation-HomeAddressLine2'?: string;
    'PersonalInformation-HomeCity'?: string;
    'PersonalInformation-HomeState'?: string;
    'PersonalInformation-HomeZipCode'?: string;
    'PersonalInformation-IsMailingHomeAddressSame'?: boolean;
    'PersonalInformation-PrimaryPhoneNumber'?: string;
    'PersonalInformation-MonthlyIncome'?: string;
    'PersonalInformation-Ssn'?: string;
    'PersonalInformation-DOBMonth'?: string;
    'PersonalInformation-DOBDay'?: string;
    'PersonalInformation-DOBYear'?: string;
    'DeviceInfo-ioBlackBox'?: string;
    'DeviceInfo-fpBB'?: string;
    'SelectedProgramPlasticCode'?: number;
    'ApplicantESignInterest'?: number;
    'AgreedToFullESign'?: boolean; // Potentially The Terms And Conditions?
    'ESignDisclosureVersion'?: string;
    'ConsumerAcknowledgment'?: boolean;
    'AgreedToPrivacyPolicy'?: boolean;
    'ProgramCode'?: string;
    'IsSecuredCard'?: boolean;
    'IsBalanceTransferSelected'?: boolean;
    'MarketingAttributes'?: TAcquisitionsMarketingAttributeArray;
    'SolicitationId'?: number;
    'ReservationNumber'?: string;
};

const useApplicationSubmit = (
    props: PropsWithChildren<IApplicationCtaProps>, 
    setShouldRenderLoader: Function,
    setIsButtonAwaitingResponse: Function,
): {
    persistData: (validatingState: TAcquisitionProviderFacade) => Promise<void>;
    applicationSubmit: (validatingState: TAcquisitionProviderFacade) => Promise<void>;
} => {

    const fetchPrequalData = async (): Promise<TPrequalResponse['ApplicationInformation'][0]> => {
        const sessionStorageProgramCode = SessionStorage.get('selectedProgramCode');
        const sessionStorageData = (await SessionStorage.tryGetCipher('prequalResponse', {}) as TPrequalResponse);
        const programCodeData = sessionStorageData?.ApplicationInformation?.find((entry) => entry?.ProgramRates?.ProgramCode === sessionStorageProgramCode);
        return programCodeData;
    }

    const writeToCurrentScope = async (
        flow: AcquisitionFlow,
        appData: Record<string, any>, // any used for compatibility with legacy code
    ): Promise<void> => {
        // TODO: Consolidate all of the flows to just use flowApplicationData.
        if ([
            AcquisitionFlow.PHA,
        ].includes(flow)) {
            SessionStorage.setObjectWithCurrentScope('flowApplicationData', appData);

        } else if ([
            AcquisitionFlow.PQ,
            AcquisitionFlow.SkipPQ,
            AcquisitionFlow.PqOfOne,
        ].includes(flow)) {
            await SessionStorage.setCipherWithCurrentScope('prequalFullAppData', appData);

        } else if (
            AcquisitionFlow.PA === flow
        ) {
            await SessionStorage.setCipherWithCurrentScope( 'preApprovalFullAppData', appData );

            if ( SessionStorage.verify('pre-approval-data') && appData.Applicant ) {
                const preApprovalData = await SessionStorage.getCipher('pre-approval-data', true );

                preApprovalData.Applicant = appData.Applicant;

                await SessionStorage.setCipher('pre-approval-data', preApprovalData );
            }
        }
    };

    const persistData = async (validatingState: TAcquisitionProviderFacade): Promise<void> => {
        const { flow } = validatingState.getCurrentMetaAcquisitionFlowState();
        const toPersist: TApplicationData = await SessionStorage.tryGetCipher<Record<string, unknown>>('persistingFlowApplicationData', {});

        const personalInfo = validatingState.getPersonalInformation();
        const phoneAndIncomeInfo = validatingState.getPhoneAndIncomeInfo();

        const personalInfoForm = validatingState.getPersonalInfoForm();
        const dobArr = personalInfoForm.dob.value.split('/');

        const socialSecurityInfo = validatingState.getSsn();

        if (hasDefaultComponent('cob-corp-acquisition/components/creditonebank/personal-information-form')) {
            toPersist['PersonalInformation-FirstName'] = personalInfo.firstName.value || personalInfoForm.firstName.value;
            toPersist['PersonalInformation-MiddleInitial'] = personalInfo.middleInitial.value || personalInfoForm.middleInitial.value;
            toPersist['PersonalInformation-LastName'] = personalInfo.lastName.value || personalInfoForm.lastName.value;
            toPersist['PersonalInformation-NameSuffix'] = personalInfo.suffix.value || personalInfoForm.suffix.value;
    
            toPersist['PersonalInformation-MailingAddressLine1'] = personalInfo.mailingAddressLineOne.value || personalInfoForm.mailingAddress.value;
            toPersist['PersonalInformation-MailingAddressLine2'] = personalInfo.mailingAddressLineTwo.value || personalInfoForm.mailingAptUnit.value || '';
            toPersist['PersonalInformation-MailingCity'] = personalInfo.mailingAddressCity.value || personalInfoForm.mailingCity.value;
            toPersist['PersonalInformation-MailingState'] = personalInfo.mailingAddressState.value || personalInfoForm.mailingState.value;
            toPersist['PersonalInformation-MailingZipCode'] = personalInfo.mailingAddressZipCode.value || personalInfoForm.mailingZip.value;
            
            toPersist['PersonalInformation-IsMailingHomeAddressSame'] = !personalInfoForm.homeAddressShown; // || personalInfo.homeAddressLineOne.value === '';
            
            toPersist['PersonalInformation-HomeAddressLine1'] = personalInfo.homeAddressLineOne.value || personalInfoForm.homeAddress.value;
            toPersist['PersonalInformation-HomeAddressLine2'] = personalInfo.homeAddressLineTwo.value || personalInfoForm.homeAptUnit.value || '';
            toPersist['PersonalInformation-HomeCity'] = personalInfo.homeAddressCity.value || personalInfoForm.homeCity.value;
            toPersist['PersonalInformation-HomeState'] = personalInfo.homeAddressState.value || personalInfoForm.homeState.value;
            toPersist['PersonalInformation-HomeZipCode'] = personalInfo.homeAddressZipCode.value || personalInfoForm.homeZip.value;
    
            toPersist['PersonalInformation-EmailAddress'] = personalInfo.emailAddress.value || personalInfoForm.email.value;
            toPersist['PersonalInformation-PrimaryPhoneNumber'] = personalInfo.phoneNumber.value || phoneAndIncomeInfo.phoneNumber.value || '';
    
            toPersist['PersonalInformation-MonthlyIncome'] = personalInfo.monthlyIncome.value || phoneAndIncomeInfo.monthlyIncome.value;
            toPersist['PersonalInformation-Ssn'] = personalInfo.ssn.value || personalInfoForm.ssn.value;
    
            toPersist['PersonalInformation-DOBMonth'] = enforceTwoDigitDOB(personalInfo.dateOfBirthMonth.value || dobArr[0]) as string;
            toPersist['PersonalInformation-DOBDay'] = enforceTwoDigitDOB(personalInfo.dateOfBirthDay.value || dobArr[1]) as string;
            toPersist['PersonalInformation-DOBYear'] = enforceTwoDigitDOB(personalInfo.dateOfBirthYear.value || dobArr[2]) as string;
        }

        if (hasDefaultComponent('cob-corp-acquisition/components/creditonebank/authorized-user')) {
            const authorizedUser = validatingState.getAuthorizedUser();
    
            toPersist['AuthorizedUser-IsAuthorizedUserSelected'] = validatingState.getIsAuthorizedUserSelected();
            toPersist['AuthorizedUser-IsAuthorizedUserSpouse'] = personalInfo.authorizedUserSpouse.value;
            toPersist['AuthorizedUser-AuthorizedUserFirstName'] = personalInfo.authorizedUserFirstName.value || authorizedUser.authorizedUserInfo.AuthorizedUserFirstName;
            toPersist['AuthorizedUser-AuthorizedUserMiddleInitial'] = personalInfo.authorizedUserMiddleInitial.value || authorizedUser.authorizedUserInfo.AuthorizedUserMiddleInitial;
            toPersist['AuthorizedUser-AuthorizedUserLastName'] = personalInfo.authorizedUserLastName.value || authorizedUser.authorizedUserInfo.AuthorizedUserLastName;
            toPersist['AuthorizedUser-AuthorizedUserDOBDay'] = personalInfo.authorizedUserDateOfBirthDay.value || authorizedUser.authorizedUserInfo.AuthorizedUserDOBDay;
            toPersist['AuthorizedUser-AuthorizedUserDOBMonth'] = personalInfo.authorizedUserDateOfBirthMonth.value || authorizedUser.authorizedUserInfo.AuthorizedUserDOBMonth;
            toPersist['AuthorizedUser-AuthorizedUserDOBYear'] = personalInfo.authorizedUserDateOfBirthYear.value || authorizedUser.authorizedUserInfo.AuthorizedUserDOBYear;
        }

        if (hasDefaultComponent('cob-corp-acquisition/components/creditonebank/credit-protection')) {
            const creditProtection = validatingState.getCreditProtectionInfo();

            toPersist['CreditProtection-CreditProtectionSelected'] = creditProtection.CreditProtectionSelected;
            toPersist['CreditProtection-CreditProtectionInitials'] = creditProtection.CreditProtectionInitials;
        }
        if (
            hasDefaultComponent('cob-corp-acquisition/components/creditonebank/phone-number-and-income')
            || hasDefaultComponent('cob-corp-acquisition/components/creditonebank/personal-information-form')
        ) {
            toPersist['PersonalInformation-MonthlyIncome'] = personalInfo.monthlyIncome.value || phoneAndIncomeInfo.monthlyIncome.value;
            toPersist['PersonalInformation-PrimaryPhoneNumber'] = personalInfo.phoneNumber.value || phoneAndIncomeInfo.phoneNumber.value;
        }
        if (hasDefaultComponent('cob-corp-acquisition/components/creditonebank/plastic-card-design')) {
            toPersist['SelectedProgramPlasticCode'] = validatingState.getSelectedCardId();
        }
        if (hasDefaultComponent('cob-corp-acquisition/components/creditonebank/terms-and-conditions')) {
            toPersist['AgreedToPrivacyPolicy'] = validatingState.getTerms().isTermsAgreed;
        }
        if (hasDefaultComponent('cob-corp-acquisition/components/creditonebank/ssn-module')) {
            toPersist['PersonalInformation-Ssn'] = personalInfo.ssn.value || personalInfoForm.ssn.value || socialSecurityInfo.ssnInfo;
        }
        if (hasDefaultComponent('cob-corp-acquisition/components/creditonebank/consumer-report')) {
            toPersist['ConsumerAcknowledgment'] = validatingState.getConsumerReport().isConsumerReportAgreed;
        }
        if (hasDefaultComponent('cob-corp-acquisition/components/creditonebank/application-esign')) {
            const esignInfo = validatingState.getEsign();

            toPersist['AgreedToFullESign'] = esignInfo.isEsignAgreed;
            toPersist['ApplicantESignInterest'] = Number(esignInfo.isEsignAgreed && esignInfo.isEsignShowing);
            toPersist['ESignDisclosureVersion'] = esignInfo.isEsignShowing && esignInfo.esignVersion ? esignInfo.esignVersion as string : '';

            toPersist['MarketingAttributes'] = getMarketingAttributesForAcquisitions();
        }

        const deviceInfo = blackboxUtils.getBlackbox();

        if (deviceInfo) {
            toPersist['DeviceInfo-ioBlackBox'] = (deviceInfo?.Blackbox?.ThirdParty || '') as string;
            toPersist['DeviceInfo-fpBB'] = (deviceInfo?.Blackbox?.FirstParty || '') as string;
        }

        const ancillarySelections = validatingState.getAncillarySelections();

        toPersist['IsBalanceTransferSelected'] = ancillarySelections.balanceTransferSelected;

        toPersist['ProgramCode'] = SessionStorage.get('selectedProgramCode');

        if (checkIfInValidAcquisitionFlow([
            AcquisitionFlow.PQ,
            AcquisitionFlow.PqOfOne,
            AcquisitionFlow.SkipPQ
        ], flow)) {
            const prequalOfferData = await fetchPrequalData();
            toPersist['IsSecuredCard'] = prequalOfferData ? prequalOfferData.IsSecuredCard : false;
        }
        
        if (checkIfInValidAcquisitionFlow([
            AcquisitionFlow.PQ,
            AcquisitionFlow.PqOfOne,
            AcquisitionFlow.SkipPQCounter,
        ], flow)) {
            const personalInfoData = (await SessionStorage.tryGetCipher<Record<string, string & boolean & unknown>>('personalInfo', {}));

            toPersist['PersonalInformation-FirstName'] = personalInfoData.FirstName;
            toPersist['PersonalInformation-MiddleInitial'] = personalInfoData.MiddleInitial;
            toPersist['PersonalInformation-LastName'] = personalInfoData.LastName;
            toPersist['PersonalInformation-NameSuffix'] = '';
    
            toPersist['PersonalInformation-MailingAddressLine1'] = personalInfoData.MailingAddressLine1;
            toPersist['PersonalInformation-MailingAddressLine2'] = personalInfoData.MailingAddressLine2;
            toPersist['PersonalInformation-MailingCity'] = personalInfoData.MailingCity;
            toPersist['PersonalInformation-MailingState'] = personalInfoData.MailingState;
            toPersist['PersonalInformation-MailingZipCode'] = personalInfoData.MailingZipCode;
            
            toPersist['PersonalInformation-IsMailingHomeAddressSame'] = personalInfoData.IsMailingHomeAddressSame;
            
            toPersist['PersonalInformation-HomeAddressLine1'] = personalInfoData.HomeAddressLine1;
            toPersist['PersonalInformation-HomeAddressLine2'] = personalInfoData.HomeAddressLine2;
            toPersist['PersonalInformation-HomeCity'] = personalInfoData.HomeCity;
            toPersist['PersonalInformation-HomeState'] = personalInfoData.HomeState;
            toPersist['PersonalInformation-HomeZipCode'] = personalInfoData.HomeZipCode;
    
            toPersist['PersonalInformation-PrimaryPhoneNumber'] = personalInfoData.PrimaryPhoneNumber || toPersist['PersonalInformation-PrimaryPhoneNumber'];
    
            toPersist['PersonalInformation-Ssn'] = personalInfoData.Ssn || toPersist['PersonalInformation-Ssn'];
    
            toPersist['PersonalInformation-DOBMonth'] = enforceTwoDigitDOB(personalInfoData.DOBMonth) as string;
            toPersist['PersonalInformation-DOBDay'] = enforceTwoDigitDOB(personalInfoData.DOBDay) as string;
            toPersist['PersonalInformation-DOBYear'] = enforceTwoDigitDOB(personalInfoData.DOBYear) as string;
        }

        if (AcquisitionFlow.PA === flow) {
            const preApprovalData = await SessionStorage.tryGetCipher<Record<string, string & unknown>>('pre-approval-data', {});
            toPersist['SolicitationId'] = parseInt(preApprovalData.SolicitationId);
            toPersist['ReservationNumber'] = preApprovalData.ReservationNumber;
        }

        await SessionStorage.setCipherWithCurrentScope('persistingFlowApplicationData', toPersist);

        const appData = await buildAppData(flow, toPersist);
        await writeToCurrentScope(flow, appData);
    }

    const retrievePersistedData = async (): Promise<TApplicationData> => {
        return await SessionStorage.tryGetCipher('persistingFlowApplicationData', {});
    }

    const buildAppData = async (
        flow: AcquisitionFlow,
        persistedData: TApplicationData,
    ): Promise<TSubmitDataPreApproved | TSubmitDataPqSkipPqPqOfOne | TSubmitDataPHA> => {
        if (checkIfInValidAcquisitionFlow([
            AcquisitionFlow.PA
        ], flow)) {
            const appData = await buildSubmitDataForPA(persistedData);
            await SessionStorage.setCipherWithCurrentScope( 'preApprovalFullAppData', appData );

            if ( SessionStorage.verify('pre-approval-data') && appData.Applicant ) {
                const preApprovalData = await SessionStorage.getCipher('pre-approval-data', true );

                preApprovalData.Applicant = appData.Applicant;

                await SessionStorage.setCipher( 'pre-approval-data', preApprovalData );
            }
            return appData;

        } else if (checkIfInValidAcquisitionFlow([
            AcquisitionFlow.PQ,
            AcquisitionFlow.PqOfOne,
            AcquisitionFlow.SkipPQ,
            AcquisitionFlow.SkipPQCounter,
        ], flow)) {
            const appData = await buildSubmitDataForPqSkipPqPqOfOne(persistedData);
            await SessionStorage.setCipherWithCurrentScope('prequalFullAppData', appData)
            return appData;
        } else if (checkIfInValidAcquisitionFlow([
                AcquisitionFlow.PHA
        ], flow)) {
            const appData = await buildSubmitFlowApplicationData(persistedData)
            SessionStorage.setObjectWithCurrentScope('flowApplicationData', appData)
            return appData
        }
    }

    const attemptSubmit = async (
        validatingState: TAcquisitionProviderFacade
    ): Promise<void> => {
        const flow = validatingState.getCurrentMetaAcquisitionFlowState().flow;
        const selectedCardId = validatingState.getSelectedCardId();
        const redirectUrl = props.defaultRedirectUrl;


        // Ensure application data is up to date for verification page
        const persistedData = await retrievePersistedData();
        const appData = await buildAppData(flow, persistedData);
        await writeToCurrentScope(flow, appData);

        const cardInfo = SessionStorage.tryGetObject('cardInfo', []);

        await flowDataModule.setInSession({
            MarketingAttributes: getMarketingAttributesForAcquisitions(),
        });

        const appDataAny = appData as Record<string, any>; // any used to allow nested objects to not cause issues.

        // Shared Properties
        let updateToFlowDataModule: Record<string, any> = {
            IsAuthorizedUserSelected: appData?.AuthorizedUser?.IsAuthorizedUserSelected || false,
            IsCreditProtectionSelected: appDataAny?.creditProtectionSelected || false,
            IsBalanceTransferSelected: appData?.IsBalanceTransferSelected || false,
            SelectedProgramPlasticCode: cardInfo[selectedCardId - 1],
        }

        // TODO: Consolidate this. Fix the typing also
        if (flow !== AcquisitionFlow.PHA) {
            updateToFlowDataModule = {
                ...updateToFlowDataModule,
                FirstName: appDataAny.PersonalInformation
                    ? appDataAny.PersonalInformation.FirstName
                    : appDataAny.Applicant.ApplicantName.FirstName,
                MonthlyIncome: appDataAny.PersonalInformation
                    ? appDataAny.PersonalInformation.MonthlyIncome
                    : appDataAny.Applicant.MonthlyIncome,
                ZipCode: appDataAny.PersonalInformation
                    ? appDataAny.PersonalInformation.MailingZipCode
                    : appDataAny.Applicant.MailingAddress.ZipCode,
                IsCreditProtectionSelected: appDataAny.CreditProtection
                    ? appDataAny.CreditProtection.CreditProtectionSelected
                    : appDataAny.CreditProtectionSelected,

            };
        } else { // Implicitly PHA
            const phaAppData = appData as TSubmitDataPHA;
            updateToFlowDataModule = {
                ...updateToFlowDataModule,
                FirstName: phaAppData?.parties?.[0]?.firstName,
                MonthlyIncome: phaAppData?.parties?.[0]?.income?.amount,
                ZipCode: phaAppData?.parties?.[0]?.addresses?.find(
                        addr => addr.usageType === 'MAILING',
                    )?.zipCode,
            };
        }

        await flowDataModule.updateInSession(updateToFlowDataModule);
        window.location.assign(`${redirectUrl}${window.location.search}`);
    };

    const applicationSubmit = async (validatingState: TAcquisitionProviderFacade): Promise<void> => {
        setShouldRenderLoader(true);
        setIsButtonAwaitingResponse(true);

        await attemptSubmit(validatingState);
    };

    return {
        persistData,
        applicationSubmit
    };
};

export default useApplicationSubmit;